/**
 * Archivo: assets/boton-flecha.js
 * JavaScript con seguridad mejorada y lazy preloading
 */

(function () {
    'use strict';

    // Configuración
    const CONFIG = {
        animationDuration: 700, // Duración de tu animación CSS
        navigationDelay: 400,   // Delay antes de navegar
        preloadOnHover: true    // Precargar al hacer hover
    };

    // SEGURIDAD: Validación estricta de URLs
    const SecurityValidator = {
        isValidURL: function (url) {
            if (!url || typeof url !== 'string') return false;

            url = url.trim();

            // Bloquear protocolos peligrosos
            const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'ftp:'];
            const lowerUrl = url.toLowerCase();

            for (const protocol of dangerousProtocols) {
                if (lowerUrl.startsWith(protocol)) {
                    console.warn('[Boton Flecha Security] Protocolo bloqueado:', url);
                    return false;
                }
            }

            // Solo permitir URLs relativas o del mismo dominio
            if (url.startsWith('/')) {
                return true; // URL relativa válida
            }

            // Verificar dominio actual
            try {
                const urlObj = new URL(url);
                const currentDomain = window.location.hostname;

                // Solo permitir mismo dominio o dominios Shopify
                const isValidDomain = urlObj.hostname === currentDomain ||
                    urlObj.hostname.endsWith('.myshopify.com') ||
                    urlObj.hostname.endsWith('.shopifypreview.com');

                if (!isValidDomain) {
                    console.warn('[Boton Flecha Security] Dominio no permitido:', urlObj.hostname);
                    return false;
                }

                return true;
            } catch (e) {
                console.warn('[Boton Flecha Security] URL inválida:', url);
                return false;
            }
        }
    };

    class ShopifyNavigation {
        constructor() {
            this.precargadas = new Set();
            this.botonesVisibles = new Set(); // Para lazy preloading
            this.observer = null; // Intersection Observer
            this.init();
        }

        init() {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupSmoothNavigation();
                this.setupLazyPreloading();
            });
        }

        setupSmoothNavigation() {
            const botones = document.querySelectorAll('[data-smooth-nav="true"]');

            botones.forEach(boton => {
                // SEGURIDAD: Validar URL antes de configurar eventos
                if (!SecurityValidator.isValidURL(boton.href)) {
                    console.warn('[Boton Flecha] URL inválida, deshabilitando funcionalidad:', boton.href);
                    boton.removeAttribute('data-smooth-nav');
                    return;
                }

                // Manejar clicks
                boton.addEventListener('click', (e) => {
                    this.handleSmoothClick(e, boton);
                });

                // Soporte para teclado
                boton.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.handleSmoothClick(e, boton);
                    }
                });

                // Precargar en hover SOLO si ya está visible (lazy)
                if (CONFIG.preloadOnHover) {
                    boton.addEventListener('mouseenter', () => {
                        if (this.botonesVisibles.has(boton)) {
                            this.precargarUrl(boton.href);
                        }
                    }, { passive: true });
                }
            });
        }

        // LAZY PRELOADING: Solo precargar botones visibles
        setupLazyPreloading() {
            if (!('IntersectionObserver' in window)) {
                // Fallback: marcar todos como visibles si no hay soporte
                document.querySelectorAll('[data-smooth-nav="true"]').forEach(boton => {
                    this.botonesVisibles.add(boton);
                });
                return;
            }

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const boton = entry.target;
                        this.botonesVisibles.add(boton);

                        // Precargar inmediatamente si tiene prioridad alta
                        if (boton.dataset.preload === 'high') {
                            this.precargarUrl(boton.href);
                        }

                        // Una vez visible, no necesitamos seguir observando
                        this.observer.unobserve(boton);
                    }
                });
            }, {
                threshold: 0.1, // Precargar cuando 10% del botón sea visible
                rootMargin: '50px' // Precargar 50px antes de que sea completamente visible
            });

            // Observar todos los botones
            document.querySelectorAll('[data-smooth-nav="true"]').forEach(boton => {
                this.observer.observe(boton);
            });
        }

        handleSmoothClick(evento, boton) {
            evento.preventDefault();

            const destino = boton.href;

            // SEGURIDAD: Re-validar URL antes de navegar
            if (!SecurityValidator.isValidURL(destino)) {
                console.error('[Boton Flecha Security] Navegación bloqueada, URL inválida:', destino);
                return;
            }

            // Marcar como navegando
            document.body.classList.add('navegando');
            boton.classList.add('animando');

            // Activar animación
            boton.focus();

            // Precargar página si no se ha hecho
            this.precargarUrl(destino);

            // Programar navegación
            setTimeout(() => {
                this.navigateToUrl(destino);
            }, CONFIG.navigationDelay);
        }

        precargarUrl(url) {
            // SEGURIDAD: Validar antes de precargar
            if (!SecurityValidator.isValidURL(url)) {
                return;
            }

            if (this.precargadas.has(url)) return;

            this.precargadas.add(url);

            // Múltiples métodos de precarga para máxima compatibilidad
            this.addPrefetchLink(url);
            this.preloadViaFetch(url);

            // Precargar recursos probables de la página
            this.preloadPageAssets(url);
        }

        addPrefetchLink(url) {
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = url;
            link.as = 'document';
            document.head.appendChild(link);
        }

        preloadViaFetch(url) {
            // Solo para páginas del mismo dominio (ya validado)
            fetch(url, {
                method: 'GET',
                credentials: 'same-origin'
            }).catch(() => {
                // Silenciosamente manejar errores
            });
        }

        preloadPageAssets(url) {
            // Precargar assets comunes que probablemente use la siguiente página
            const commonAssets = [
                '/assets/theme.css',
                '/assets/section-collection-template.css',
                '/assets/component-card.css'
            ];

            commonAssets.forEach(asset => {
                if (asset && !document.querySelector(`link[href="${asset}"]`)) {
                    const link = document.createElement('link');
                    link.rel = 'preload';
                    link.href = asset;
                    link.as = 'style';
                    document.head.appendChild(link);
                }
            });
        }

        navigateToUrl(url) {
            // SEGURIDAD: Validación final antes de navegar
            if (!SecurityValidator.isValidURL(url)) {
                console.error('[Boton Flecha Security] Navegación final bloqueada:', url);
                return;
            }

            // Navegación directa sin transiciones
            window.location.href = url;
        }
    }

    // Inicializar para toda la tienda
    new ShopifyNavigation();

    // Exponer funciones globales para uso en otros scripts
    window.ShopifySmooth = {
        preload: function (url) {
            // SEGURIDAD: Validar URL antes de precargar
            if (SecurityValidator.isValidURL(url)) {
                if (window.shopifyNav) {
                    window.shopifyNav.precargarUrl(url);
                }
            } else {
                console.warn('[Boton Flecha Security] Precarga bloqueada, URL inválida:', url);
            }
        },

        isValidURL: SecurityValidator.isValidURL
    };

})();